"use client";

import { Card, Icon } from "@mui/material";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import useLanguage from "@/contexts/language-context";

import { NavigationListItem } from "./types";

type Props<T = string> = {
  items: NavigationListItem<T>[];
  selectedItem?: T;
  onChangeSelection?: (item: NavigationListItem<T>) => void;
};

function NavigationList<T>({ items, selectedItem, onChangeSelection }: Props<T>) {
  const router = useNavigate();
  const { lang } = useLanguage();

  function handleOnChangeSelection(item: NavigationListItem<T>) {
    const { href } = item;
    const pathname = href(lang);

    if (onChangeSelection) {
      onChangeSelection(item);
    } else router(pathname);
  }

  return (
    <Card
      sx={{
        borderRadius: ({ borders: { borderRadius } }) => borderRadius.lg,
        position: "sticky",
        top: "1%",
      }}
    >
      <MDBox
        component="ul"
        display="flex"
        flexDirection="column"
        p={2}
        m={0}
        sx={{ listStyle: "none" }}
      >
        {items.map((item, index) => {
          const { icon, title, _id, selectedIcon } = item;
          const isSelected = _id === selectedItem;
          const displayIcon = isSelected ? selectedIcon : icon;

          return (
            <MDBox key={`${_id}-${index}`} component="li" pt={index === 0 ? 0 : 1}>
              <MDTypography
                component="a"
                onClick={() => handleOnChangeSelection(item)}
                variant="button"
                fontWeight="regular"
                textTransform="capitalize"
                sx={({
                  borders: { borderRadius },
                  functions: { pxToRem },
                  palette: { light, primary },
                  transitions,
                }) => ({
                  display: "flex",
                  alignItems: "center",
                  borderRadius: borderRadius.md,
                  padding: `${pxToRem(10)} ${pxToRem(16)}`,
                  transition: transitions.create("background-color", {
                    easing: transitions.easing.easeInOut,
                    duration: transitions.duration.shorter,
                  }),
                  cursor: "pointer",
                  color: isSelected ? primary.main : "text.primary",
                  "&:hover": {
                    backgroundColor: light.main,
                  },
                })}
              >
                <MDBox mr={1.5} lineHeight={1} color={isSelected ? "primary" : "text"}>
                  <Icon fontSize="small">{displayIcon}</Icon>
                </MDBox>
                {title}
              </MDTypography>
            </MDBox>
          );
        })}
      </MDBox>
    </Card>
  );
}

export default NavigationList;
